import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import "./NcdForm.css";
import { ncdAPI } from "../../../services/ncd.api";
import { useNCD } from "../hooks/useNCD";
import { formatToInputDate, formatToApiDate } from "../../../helpers/common-helper";

export const BreastScreeningCHO = ({ individualId, breastScreeningId, viewMode, onIdUpdate, selectedUser,setUserError }) => {
   
    const {screeningData, setLoading, setScreeningData, setError, clearScreeningData } = useNCD();
    const today = new Date().toISOString().split("T")[0];
    const breastData = screeningData[`breast_${individualId}`];
    
    const [dotLabels, setDotLabels] = useState({});

    const mapPointsToDots = (examination = {}) => {
        const labels = {};
        
        const addLabel = (key, label) => {
            if (!labels[key]) labels[key] = [];
            if (!labels[key].includes(label)) labels[key].push(label);
        };
        
        // Map lump points
        if (examination.lumpPoints) {
            examination.lumpPoints.forEach(point => {
                const side = point.startsWith("RB") ? "right" : "left";
                const dotNum = point.replace("RB", "").replace("LB", "");
                const key = `${side}-dot${dotNum}`;
                addLabel(key, "Lump");
            });
        }
        
        // Map ulcer points
        if (examination.ulcerPoints) {
            examination.ulcerPoints.forEach(point => {
                const side = point.startsWith("RB") ? "right" : "left";
                const dotNum = point.replace("RB", "").replace("LB", "");
                const key = `${side}-dot${dotNum}`;
                addLabel(key, "Ulceration");
            });
        }
        
        // Map nipple retraction points
        if (examination.nippleRetractionPoints) {
            examination.nippleRetractionPoints.forEach(point => {
                const side = point.startsWith("RB") ? "right" : "left";
                const dotNum = point.replace("RB", "").replace("LB", "");
                const key = `${side}-dot${dotNum}`;
                addLabel(key, "Retraction of Nipple");
            });
        }
        
        // Map skin dimpling points
        if (examination.skinDimplingPoints) {
            examination.skinDimplingPoints.forEach(point => {
                const side = point.startsWith("RB") ? "right" : "left";
                const dotNum = point.replace("RB", "").replace("LB", "");
                const key = `${side}-dot${dotNum}`;
                addLabel(key, "Skin Dimpling/Puckering");
            });
        }
        
        // Map nipple discharge points
        if (examination.nippleDischargePoints) {
            examination.nippleDischargePoints.forEach(point => {
                const side = point.startsWith("RB") ? "right" : "left";
                const dotNum = point.replace("RB", "").replace("LB", "");
                const key = `${side}-dot${dotNum}`;
                addLabel(key, "Nipple Discharge");
            });
        }
        
        return labels;
    };

    useEffect(() => {
        // Simulate getting data from API
        const fetchData = async () => {
            try {
                setLoading(true);
                const responseData = await ncdAPI.getBreastScreeningCHO(individualId, breastScreeningId);
                setScreeningData(`breast_${individualId}`, responseData?.data);
                const labels = mapPointsToDots(responseData?.data?.examination);
                setDotLabels(labels);
                
                // Set selected findings based on examination data
                const findings = {};
                if (responseData?.data?.examination?.lump) findings["Lump"] = "Yes";
                if (responseData?.data?.examination?.ulcer) findings["Ulceration"] = "Yes";
                if (responseData?.data?.examination?.nippleRetraction) findings["Retraction of Nipple"] = "Yes";
                if (responseData?.data?.examination?.skinDimpling) findings["Skin Dimpling/Puckering"] = "Yes";
                if (responseData?.data?.examination?.nippleDischarge) findings["Nipple Discharge"] = "Yes";
                setSelectedFindings(findings);
                setLoading(false);
            } catch (err) {
                setError("Failed to fetch breast screening data");
                setLoading(false);
            }
        };

        if (viewMode === 'VIEW' && breastScreeningId) {
            fetchData();
        }
    }, [viewMode, breastScreeningId, individualId]);

    ////////////////////////////End Breast js////////////////////////

    const [formData, setFormData] = useState({
        assessmentDate: today,
        examinationComments: null,
        exam: {
            examDate: '',
            noAbnormalityFound: false,
            shapeSizeChangeInBreast: false,
            nippleLevelNotSame: false,
            skinEdema: false,
            isLessSkinEdma: false,
            isMoreSkinEdma: false,
            lymphadenopathy: false,
            axillary: false,
            supraclavicular: false,
            infraclavicular: false,
            nodeAxillary: '',
            nodeSupraclavicular: '',
            examLocationPoints: {
                lumpPoints: [],
                ulcerPoints: [],
                nippleRetractionPoints: [],
                nippleDischargePoints: [],
                dimplingPoints: [],
                retractionSkinPoints: []
            },
            dimpling: null,
            skinRetraction: null,
            ulceration: null,
            nipplesRetraction: null,
            lump: false,
            discharge: null
        }
    });


    const handleSubmit = async (e) => {
        e.preventDefault();

         if (!selectedUser) {
            setUserError(true);
        }

        // Get user details
        const username = selectedUser || "";
        const anmValue = selectedUser || "";

        // Symptoms fields
        const symptoms = {
            date: formatToApiDate(formData.assessmentDate),
            anyIssuesInBreast: hasBreastSymptoms === "yes",
            lumpOrThickening: formData.lumpOrThickening === "yes",
            changeShape: formData.changeShape === "yes",
            nippleDischarge: formData.nippleDischarge1 === "yes",
            changeSize: formData.changeSize === "yes",
            nippleRetraction: formData.nippleRetraction1 === "yes",
            swellingArmpit: formData.swellingArmpit === "yes",
            puckering: formData.puckering === "yes",
            rednessOrUlcer: formData.rednessOrUlcer === "yes",
            nippleErosion: formData.nippleErosion === "yes",
            painInBreastArmpit: formData.painInBreastArmpit === "yes",
            remarks: formData.symptomRemarks || ""
        };

        // Visual examination fields
        const examination = {
            refusedScreening: isScreeningRefused,
            trainedSelfBreastExam: formData.trainedSelfBreastExam === "yes",
            lump: selectedFindings["Lump"] === "Yes",
            lumpPoints: Object.entries(dotLabels)
                .filter(([_, v]) => (Array.isArray(v) ? v.includes("Lump") : v === "Lump"))
                .map(([k]) => k.startsWith("left") ? k.replace("left-dot", "RB") : k.replace("right-dot", "LB")),
            nippleRetraction: selectedFindings["Retraction of Nipple"] === "Yes",
            nippleRetractionPoints: Object.entries(dotLabels)
                .filter(([_, v]) => (Array.isArray(v) ? v.includes("Retraction of Nipple") : v === "Retraction of Nipple"))
                .map(([k]) => k.startsWith("left") ? k.replace("left-dot", "RB") : k.replace("right-dot", "LB")),
            skinDimpling: selectedFindings["Skin Dimpling/Puckering"] === "Yes",
            skinDimplingPoints: Object.entries(dotLabels)
                .filter(([_, v]) => (Array.isArray(v) ? v.includes("Skin Dimpling/Puckering") : v === "Skin Dimpling/Puckering"))
                .map(([k]) => k.startsWith("left") ? k.replace("left-dot", "RB") : k.replace("right-dot", "LB")),
            ulcer: selectedFindings["Ulceration"] === "Yes",
            ulcerPoints: Object.entries(dotLabels)
                .filter(([_, v]) => (Array.isArray(v) ? v.includes("Ulceration") : v === "Ulceration"))
                .map(([k]) => k.startsWith("left") ? k.replace("left-dot", "RB") : k.replace("right-dot", "LB")),
            nippleDischarge: selectedFindings["Nipple Discharge"] === "Yes",
            nippleDischargePoints: Object.entries(dotLabels)
                .filter(([_, v]) => (Array.isArray(v) ? v.includes("Nipple Discharge") : v === "Nipple Discharge"))
                .map(([k]) => k.startsWith("left") ? k.replace("left-dot", "RB") : k.replace("right-dot", "LB")),
            armpitSwellingLeft: formData["mat-checkbox-6-input"] ? "L" : null,
            armpitSwellingRight: formData["mat-checkbox-7-input"] ? "R" : null,
            remarks: formData.visualRemarks || ""
        };

        // Referral fields
        const referral = {
            referred: referBreast === "Yes",
            referredTo: referBreastTo || null,
            referredToIfOther: referralOther || null,
            remarks: referralRemarks
        };

        const payload = {
            symptoms,
            examination,
            referral,
            createdUser: username,
            screeningANM: {
                users: username,
                anmValue: anmValue,
                viaTrained: null
            },
            currentScreeningId: breastScreeningId || null,
            screeningType: "FirstTime Screening"
        };

        try {
            setLoading(true);
            const response = await ncdAPI.submitBreastScreeningCHO(individualId, payload);

            if (response.success) {
                if (response?.data?.screeningId) {
                    onIdUpdate(response?.data?.screeningId);
                }
                toast.success(`Breast cancer screening data has saved successfully`);

                clearScreeningData(`breast_${individualId}`); // Clear cache to ensure fresh data on next view
                
                // Reset form fields
                setFormData({
                    assessmentDate: today,
                    examinationComments: null,
                    exam: {
                        examDate: '',
                        noAbnormalityFound: false,
                        shapeSizeChangeInBreast: false,
                        nippleLevelNotSame: false,
                        skinEdema: false,
                        isLessSkinEdma: false,
                        isMoreSkinEdma: false,
                        lymphadenopathy: false,
                        axillary: false,
                        supraclavicular: false,
                        infraclavicular: false,
                        nodeAxillary: '',
                        nodeSupraclavicular: '',
                        examLocationPoints: {
                            lumpPoints: [],
                            ulcerPoints: [],
                            nippleRetractionPoints: [],
                            nippleDischargePoints: [],
                            dimplingPoints: [],
                            retractionSkinPoints: []
                        },
                        dimpling: null,
                        skinRetraction: null,
                        ulceration: null,
                        nipplesRetraction: null,
                        lump: false,
                        discharge: null
                    }
                });
                setHasBreastSymptoms("no");
                setIsScreeningRefused(false);
                setSelectedFindings({});
                setDotLabels({});
                setDropdownValue("");
                setreferBreast("");
                setReferBreastTo("");
                setReferralRemarks("");
                setReferralOther("");
            } else {
                toast.error(response.message || "Failed to save data", { className: "custom-toast-error" });
            }
            setLoading(false);
        } catch (error) {
            toast.error(error.response?.data?.message || "Something went wrong", { className: "custom-toast-error" });
            setLoading(false);
        }

    };

    //  ============================================Jsx for yes no ========================================

    const [hasBreastSymptoms, setHasBreastSymptoms] = useState("no");

    const handleBreastchoChange = (e) => {
        setHasBreastSymptoms(e.target.value);
    };

    const isDisabled = hasBreastSymptoms === "no";

    const [referBreast, setreferBreast] = useState("");
    const [referBreastTo, setReferBreastTo] = useState("");
    const [referralRemarks, setReferralRemarks] = useState("");
    const [referralOther, setReferralOther] = useState("");

    const handleReferBreastChange = (e) => {
        const value = e.target.value;
        setreferBreast(value);
        if (value === "No") {
            setReferBreastTo("");
            setReferralOther("");
        }
    };

    const handleReferBreastToChange = (value) => {
        setReferBreastTo(value);
        if (value !== "Others") {
            setReferralOther("");
        }
    };

    const [selectedFindings, setSelectedFindings] = useState({});
    const [dropdownValue, setDropdownValue] = useState("");

    const breastSymptoms = [
        { name: "Lump", label: "Lump in breast?", viewname: "lump" },
        { name: "Ulceration", label: "Ulceration?", viewname: "ulcer" },
        { name: "Retraction of Nipple", label: "Nipple retraction/distortion?", viewname: "nippleRetraction" },
        { name: "Skin Dimpling/Puckering", label: "Skin dimpling/puckering?", viewname: "skinDimpling" },
        { name: "Nipple Discharge", label: "Discharge from nipple?", viewname: "nippleDischarge" },
    ];

    const handleBreastSymptomChange = (name, value) => {
        setSelectedFindings((prev) => {
            const updated = { ...prev };
            if (value === "Yes") {
                updated[name] = value;
            } else {
                delete updated[name];
                // Remove dots for this finding when deselected
                setDotLabels(prevDots => {
                    const updatedDots = { ...prevDots };
                    Object.keys(updatedDots).forEach(key => {
                        const labels = updatedDots[key];
                        if (Array.isArray(labels)) {
                            const filtered = labels.filter(l => l !== name);
                            if (filtered.length > 0) {
                                updatedDots[key] = filtered;
                            } else {
                                delete updatedDots[key];
                            }
                        } else if (labels === name) {
                            delete updatedDots[key];
                        }
                    });
                    return updatedDots;
                });
            }
            return updated;
        });
    };

    const getDropdownOptions = useCallback(() => Object.keys(selectedFindings), [selectedFindings]);

    useEffect(() => {
        const options = getDropdownOptions();
        if (!dropdownValue || !options.includes(dropdownValue)) {
            if (options.length > 0) {
                setDropdownValue(options[0]);
            } else {
                setDropdownValue(""); // Clear if no options
            }
        }
    }, [selectedFindings, dropdownValue, getDropdownOptions]);

    const handleDropdownChange = (e) => {
        const value = e.target.value;
        setDropdownValue(value);
    };

    const handleBreastDotClick = (dotId, isCenter = false) => {
        if (!dropdownValue || dropdownValue === "Select Findings") return;

        const isSpecial =
            dropdownValue === "Nipple Discharge" ||
            dropdownValue === "Retraction of Nipple";

        if (isSpecial && !dotId.includes("dot13")) return;

        setDotLabels((prev) => {
            const updated = { ...prev };
            const currentLabels = updated[dotId] || [];
            const labelsArray = Array.isArray(currentLabels) ? currentLabels : [currentLabels];
            
            // Toggle: if already has this finding, remove it; else, add it
            if (labelsArray.includes(dropdownValue)) {
                const filtered = labelsArray.filter(l => l !== dropdownValue);
                if (filtered.length > 0) {
                    updated[dotId] = filtered;
                } else {
                    delete updated[dotId];
                }
            } else {
                updated[dotId] = [...labelsArray, dropdownValue];
            }
            return updated;
        });
    };

    const renderDot = (side, dotId, isCenter = false, count = null) => {
        // If center dot, use dot13
        const realDotId = isCenter ? "dot13" : dotId;
        const key = `${side}-${realDotId}`;
        const labels = dotLabels[key];
        const labelsArray = labels ? (Array.isArray(labels) ? labels : [labels]) : [];
        const isActive = labelsArray.length > 0;
        
        return (
            <div
                key={key}
                className={`dot ${realDotId} ${isActive ? "active" : ""}`}
                onClick={() => handleBreastDotClick(key, isCenter)}
            >
                {count !== null && <div className="dot-count">{count}</div>}
                {isActive && <span className="dot-label">{labelsArray.join(", ")}</span>}
            </div>
        );
    };

    const dotIds = [
        "dot1", "dot2", "dot3", "dot4", "dot5", "dot6",
        "dot7", "dot8", "dot9", "dot10", "dot11", "dot12"
    ];

    const [isScreeningRefused, setIsScreeningRefused] = useState(false);

    // Helper for radio/checkbox/text fields to update formData state
    const handleFieldChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));
    };

    // For symptoms textarea (remarks)
    const handleSymptomRemarksChange = (e) => {
        setFormData(prev => ({
            ...prev,
            symptomRemarks: e.target.value
        }));
    };

    // For visual examination textarea (remarks)
    const handleVisualRemarksChange = (e) => {
        setFormData(prev => ({
            ...prev,
            visualRemarks: e.target.value
        }));
    };

    // For armpit swelling checkboxes
    const handleArmpitSwellingChange = (e) => {
        const { name, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    return (
        <>

            {viewMode === "ADD" ? (
                <form onSubmit={handleSubmit}>
                    <div className="oral-cancer-table">
                        <div className="row">
                            <div className="col-xs-12 col-md-8"></div>
                            <div className="col-xs-12 col-md-4">
                                <input
                                    type="date"
                                    className="form-control"
                                    value={formData.assessmentDate}
                                     max={new Date().toISOString().split('T')[0]}
                                    onChange={e =>
                                        setFormData(prev => ({
                                            ...prev,
                                            assessmentDate: e.target.value
                                        }))
                                    }
                                    required
                                />
                            </div>
                        </div>

                        <div className="headerTitle">
                            <div className="titleSection"><h4>Symptoms</h4></div>
                        </div>
                        <div className="choCancerFeildsSection">
                            <div className="row">
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/Any_breast_related_symptoms.png"} alt="Any breast related symptoms" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Any breast related symptoms?<span className="Refre-red">*</span></p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="anyIssuesInBreast" className="form-check-input" value="yes" onChange={handleBreastchoChange} />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="anyIssuesInBreast" className="form-check-input" value="no" onChange={handleBreastchoChange} />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/lump_thickening_in_breast.png"} alt="Lump or thickening in breast" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Lump or thickening in breast?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="lumpOrThickening"
                                                        className="form-check-input"
                                                        value="yes"
                                                        checked={formData.lumpOrThickening === "yes"}
                                                        onChange={handleFieldChange}
                                                        disabled={isDisabled}
                                                    />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="lumpOrThickening"
                                                        className="form-check-input"
                                                        value="no"
                                                        checked={formData.lumpOrThickening === "no"}
                                                        onChange={handleFieldChange}
                                                        disabled={isDisabled}
                                                    />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/change_in_shape.png"} alt="Change in shape" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Change in shape and position of nipple?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="changeShape"
                                                        className="form-check-input"
                                                        value="yes"
                                                        checked={formData.changeShape === "yes"}
                                                        onChange={handleFieldChange}
                                                        disabled={isDisabled}
                                                    />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="changeShape"
                                                        className="form-check-input"
                                                        value="no"
                                                        checked={formData.changeShape === "no"}
                                                        onChange={handleFieldChange}
                                                        disabled={isDisabled}
                                                    />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/change_in_size.png"} alt="Change in size" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Change in size?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="changeSize"
                                                        className="form-check-input"
                                                        value="yes"
                                                        checked={formData.changeSize === "yes"}
                                                        onChange={handleFieldChange}
                                                        disabled={isDisabled}
                                                    />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="changeSize"
                                                        className="form-check-input"
                                                        value="no"
                                                        checked={formData.changeSize === "no"}
                                                        onChange={handleFieldChange}
                                                        disabled={isDisabled}
                                                    />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/discharge.png"} alt="Discharge" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Discharge from one or both nipples?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="nippleDischarge1"
                                                        className="form-check-input"
                                                        value="yes"
                                                        checked={formData.nippleDischarge1 === "yes"}
                                                        onChange={handleFieldChange}
                                                        disabled={isDisabled}
                                                    />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="nippleDischarge1"
                                                        className="form-check-input"
                                                        value="no"
                                                        checked={formData.nippleDischarge1 === "no"}
                                                        onChange={handleFieldChange}
                                                        disabled={isDisabled}
                                                    />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/Any_breast_related_symptoms.png"} alt="Breast symptoms" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Any retraction of nipple?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="nippleRetraction1"
                                                        className="form-check-input"
                                                        value="yes"
                                                        checked={formData.nippleRetraction1 === "yes"}
                                                        onChange={handleFieldChange}
                                                        disabled={isDisabled}
                                                    />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="nippleRetraction1"
                                                        className="form-check-input"
                                                        value="no"
                                                        checked={formData.nippleRetraction1 === "no"}
                                                        onChange={handleFieldChange}
                                                        disabled={isDisabled}
                                                    />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/swelling.png"} alt="Swelling" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Swelling/lump in armpit?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="swellingArmpit"
                                                        className="form-check-input"
                                                        value="yes"
                                                        checked={formData.swellingArmpit === "yes"}
                                                        onChange={handleFieldChange}
                                                        disabled={isDisabled}
                                                    />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="swellingArmpit"
                                                        className="form-check-input"
                                                        value="no"
                                                        checked={formData.swellingArmpit === "no"}
                                                        onChange={handleFieldChange}
                                                        disabled={isDisabled}
                                                    />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/puckering.png"} alt="Puckering" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Puckering or dimpling?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="puckering"
                                                        className="form-check-input"
                                                        value="yes"
                                                        checked={formData.puckering === "yes"}
                                                        onChange={handleFieldChange}
                                                        disabled={isDisabled}
                                                    />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="puckering"
                                                        className="form-check-input"
                                                        value="no"
                                                        checked={formData.puckering === "no"}
                                                        onChange={handleFieldChange}
                                                        disabled={isDisabled}
                                                    />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/redness.png"} alt="Redness" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Redness of skin over breast or any ulcer?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="rednessOrUlcer"
                                                        className="form-check-input"
                                                        value="yes"
                                                        checked={formData.rednessOrUlcer === "yes"}
                                                        onChange={handleFieldChange}
                                                        disabled={isDisabled}
                                                    />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="rednessOrUlcer"
                                                        className="form-check-input"
                                                        value="no"
                                                        checked={formData.rednessOrUlcer === "no"}
                                                        onChange={handleFieldChange}
                                                        disabled={isDisabled}
                                                    />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/constant_pain_in_breast_armpit.png"} alt="Constant pain" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Constant pain in breast or armpit?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="painInBreastArmpit"
                                                        className="form-check-input"
                                                        value="yes"
                                                        checked={formData.painInBreastArmpit === "yes"}
                                                        onChange={handleFieldChange}
                                                        disabled={isDisabled}
                                                    />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="painInBreastArmpit"
                                                        className="form-check-input"
                                                        value="no"
                                                        checked={formData.painInBreastArmpit === "no"}
                                                        onChange={handleFieldChange}
                                                        disabled={isDisabled}
                                                    />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/breast_discharge.png"} alt="Breast discharge" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Erosion of nipple?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="nippleErosion"
                                                        className="form-check-input"
                                                        value="yes"
                                                        checked={formData.nippleErosion === "yes"}
                                                        onChange={handleFieldChange}
                                                        disabled={isDisabled}
                                                    />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="nippleErosion"
                                                        className="form-check-input"
                                                        value="no"
                                                        checked={formData.nippleErosion === "no"}
                                                        onChange={handleFieldChange}
                                                        disabled={isDisabled}
                                                    />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/Remarks.png"} alt="Remarks" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Remarks</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <textarea
                                                    type="text"
                                                    rows="3"
                                                    value={formData.symptomRemarks || ""}
                                                    onChange={handleSymptomRemarksChange}
                                                ></textarea>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="headerTitle ">
                            <div className="titleSection"><h4>Visual Examination</h4></div>
                        </div>
                        <div className="choCancerFeildsSection">
                            <div className="row">
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>&nbsp;</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="checkbox" name="trainedSelfBreastExam" onChange={(e) => setIsScreeningRefused(e.target.checked)} />
                                                    <span>Patient has refused screening</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/trained_individual_for_self_breast_exam.png"} alt="Trained individual" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Trained individual for self breast exam?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="trainedSelfBreastExam" className="form-check-input" disabled={isScreeningRefused} />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="trainedSelfBreastExam" className="form-check-input" disabled={isScreeningRefused} />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/Any_breast_related_symptoms.png"} alt="Breast symptoms" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Swelling in armpit?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        name="mat-checkbox-6-input"
                                                        checked={!!formData["mat-checkbox-6-input"]}
                                                        onChange={handleArmpitSwellingChange}
                                                        disabled={isScreeningRefused}
                                                    />
                                                    <span>Left</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        name="mat-checkbox-7-input"
                                                        checked={!!formData["mat-checkbox-7-input"]}
                                                        onChange={handleArmpitSwellingChange}
                                                        disabled={isScreeningRefused}
                                                    />
                                                    <span>Right</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {breastSymptoms.map((symptom) => (
                                    <div className="col-xs-12 col-md-4" key={symptom.name}>
                                        <div className="SymptomsFeild">
                                            <div className="symptomsImg">
                                                <img src={"/images/Any_breast_related_symptoms.png"} alt={symptom.label} />
                                            </div>
                                            <div className="symptomsDetaisl">
                                                <div className="symbtomsLabel">
                                                    <p>{symptom.label}</p>
                                                </div>
                                                <div className="symtomeFeildsForm">
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name={symptom.name}
                                                            className="form-check-input"
                                                            value="Yes"
                                                            onChange={e =>
                                                                handleBreastSymptomChange(symptom.name, e.target.value)
                                                            }
                                                            disabled={isScreeningRefused}
                                                        />
                                                        <span>Yes</span>
                                                    </label>
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name={symptom.name}
                                                            className="form-check-input"
                                                            value="No"
                                                            onChange={e =>
                                                                handleBreastSymptomChange(symptom.name, e.target.value)
                                                            }
                                                            disabled={isScreeningRefused}
                                                        />
                                                        <span>No</span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <div className="col-xs-12 col-md-12">
                                    <div className="SymptomsFeild remarksRow">
                                        <div className="symptomsImg">
                                            <img src={"/images/Remarks.png"} alt="Remarks" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Remarks</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <textarea
                                                    type="text"
                                                    rows="3"
                                                    value={formData.visualRemarks || ""}
                                                    onChange={handleVisualRemarksChange}
                                                ></textarea>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="breastBoxSection">
                            <div className="headerSectionBreastCHO">
                                <div className="feildsArea">
                                    <select onChange={handleDropdownChange} value={dropdownValue}>

                                        {getDropdownOptions().length === 0 ? (
                                            <option value="">Select Findings</option>
                                        ) : (
                                            <>
                                                {getDropdownOptions().map((item, idx) => (
                                                    <option key={idx}>{item}</option>
                                                ))}
                                            </>
                                        )}

                                    </select>
                                </div>
                                <div className="breastheadings">
                                    <h5>Select findings from dropdown and mark the points below.</h5>
                                </div>
                            </div>
                            <div className="breastCanserCheck">
                                <div className="leftBreast">
                                    <h5>Right</h5>
                                    <div className="wrapper">
                                        <div className="ellipse" />
                                        <div className="line vertical" />
                                        <div className="line horizontal" />
                                        {/* {dotIds.map((dot) => renderDot("right", dot))}
                                        {renderDot("right", "dot13", true)} */}

                                        {dotIds.map((dot, index) => renderDot("right", dot, false, index + 1))}
                                        {renderDot("right", "dot13", true, dotIds.length + 1)}

                                    </div>
                                </div>
                                <div className="rightBreast">
                                    <h5>Left</h5>
                                    <div className="wrapper">
                                        <div className="ellipse" />
                                        <div className="line vertical" />
                                        <div className="line horizontal" />
                                        {/* {dotIds.map((dot) => renderDot("left", dot))}
                                        {renderDot("left", "dot13", true)} */}

                                        {dotIds.map((dot, index) => renderDot("left", dot, false, index + 1))}
                                        {renderDot("left", "dot13", true, dotIds.length + 1)}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="headerTitle ">
                            <div className="titleSection"><h4>Referral</h4></div>
                        </div>
                        <div className="choCancerFeildsSection">
                            <div className="row">
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/Refer.png"} alt="Refer" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Refer</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="breastRefer" className="form-check-input" value="Yes" checked={referBreast === "Yes"} onChange={handleReferBreastChange} />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="breastRefer" className="form-check-input" value="No" checked={referBreast === "No"} onChange={handleReferBreastChange} />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-6">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/Refer.png"} alt="Refer" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Refer to {referBreast === "Yes" && <span className="text-danger">*</span>} </p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <select
                                                    value={referBreastTo}
                                                    onChange={e => handleReferBreastToChange(e.target.value)}
                                                    disabled={referBreast !== "Yes"}
                                                >
                                                    <option value="">Select</option>
                                                    <option value="PHC">PHC</option>
                                                    <option value="CHC">CHC</option>
                                                    <option value="District hospital">District Hospital</option>
                                                    <option value="Others">Other</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>If other, specify</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <input
                                                    type="text"
                                                    value={referralOther}
                                                    onChange={e => setReferralOther(e.target.value)}
                                                    disabled={referBreastTo !== "Others"}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-12">
                                    <div className="SymptomsFeild remarksRow">
                                        <div className="symptomsImg">
                                            <img src={"/images/Remarks.png"} alt="Remarks" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Remarks</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <textarea type="text" rows="3" value={referralRemarks} onChange={e => setReferralRemarks(e.target.value)} ></textarea>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="save-button-container">
                            <button type="submit" className="save-button">Save & Continue</button>
                        </div>
                    </div>
                </form>
            ) : (
                <>
                    <div className="oral-cancer-table">
                        <div className="row">
                            <div className="col-xs-12 col-md-8"></div>
                            <div className="col-xs-12 col-md-4">
                                <input
                                    type="date"
                                    className="form-control"
                                    value={formatToInputDate(breastData?.symptoms?.date)}
                                    disabled
                                />
                            </div>
                        </div>

                        <div className="headerTitle">
                            <div className="titleSection"><h4>Symptoms</h4></div>
                        </div>
                        <div className="choCancerFeildsSection">
                            <div className="row">
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/Any_breast_related_symptoms.png"} alt="Breast symptoms" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Any breast related symptoms?<span className="Refre-red">*</span></p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="anyIssuesInBreast" className="form-check-input" checked={breastData?.symptoms?.anyIssuesInBreast === true} disabled />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="anyIssuesInBreast" className="form-check-input" checked={breastData?.symptoms?.anyIssuesInBreast === false} disabled />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/lump_thickening_in_breast.png"} alt="Lump thickening" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Lump or thickening in breast?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="lumpOrThickening"
                                                        className="form-check-input"
                                                        checked={breastData?.symptoms?.lumpOrThickening === true} disabled
                                                    />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="lumpOrThickening"
                                                        className="form-check-input"
                                                        checked={breastData?.symptoms?.lumpOrThickening === false} disabled
                                                    />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/change_in_shape.png"} alt="Change in shape" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Change in shape and position of nipple?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="changeShape"
                                                        className="form-check-input"
                                                        checked={breastData?.symptoms?.changeShape === true} disabled
                                                    />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="changeShape"
                                                        className="form-check-input"
                                                        checked={breastData?.symptoms?.changeShape === false} disabled
                                                    />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/change_in_size.png"} alt="Change in size" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Change in size?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="changeSize"
                                                        className="form-check-input"
                                                        checked={breastData?.symptoms?.changeSize === true} disabled
                                                    />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="changeSize"
                                                        className="form-check-input"
                                                        checked={breastData?.symptoms?.changeSize === false} disabled
                                                    />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/discharge.png"} alt="Discharge" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Discharge from one or both nipples?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="nippleDischarge1"
                                                        className="form-check-input"
                                                        checked={breastData?.symptoms?.nippleErosion === true} disabled
                                                    />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="nippleDischarge1"
                                                        className="form-check-input"
                                                        checked={breastData?.symptoms?.nippleErosion === false} disabled
                                                    />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/Any_breast_related_symptoms.png"} alt="Breast symptoms" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Any retraction of nipple?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="nippleRetraction1"
                                                        className="form-check-input"
                                                        checked={breastData?.symptoms?.nippleRetraction === true} disabled
                                                    />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="nippleRetraction1"
                                                        className="form-check-input"
                                                        checked={breastData?.symptoms?.nippleRetraction === false} disabled
                                                    />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/swelling.png"} alt="Swelling" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Swelling lump in armpit?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="swellingArmpit"
                                                        className="form-check-input"
                                                        checked={breastData?.symptoms?.swellingArmpit === true} disabled
                                                    />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="swellingArmpit"
                                                        className="form-check-input"
                                                        checked={breastData?.symptoms?.swellingArmpit === false} disabled
                                                    />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/puckering.png"} alt="Puckering" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Puckering or dimpling?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="puckering"
                                                        className="form-check-input"
                                                        checked={breastData?.symptoms?.puckering === true} disabled
                                                    />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="puckering"
                                                        className="form-check-input"
                                                        checked={breastData?.symptoms?.puckering === false} disabled
                                                    />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/redness.png"} alt="Redness" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Redness of skin over breast or any ulcer?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="rednessOrUlcer"
                                                        className="form-check-input"
                                                        checked={breastData?.symptoms?.rednessOrUlcer === true} disabled
                                                    />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="rednessOrUlcer"
                                                        className="form-check-input"
                                                        checked={breastData?.symptoms?.rednessOrUlcer === false} disabled
                                                    />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/constant_pain_in_breast_armpit.png"} alt="Constant pain" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Constant pain in breast or armpit?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="painInBreastArmpit"
                                                        className="form-check-input"
                                                        checked={breastData?.symptoms?.painInBreastArmpit === true} disabled
                                                    />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="painInBreastArmpit"
                                                        className="form-check-input"
                                                        checked={breastData?.symptoms?.painInBreastArmpit === false} disabled
                                                    />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/breast_discharge.png"} alt="Breast discharge" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Erosion of nipple?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="nippleErosion"
                                                        className="form-check-input"
                                                        checked={breastData?.symptoms?.nippleErosion === true} disabled
                                                    />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="nippleErosion"
                                                        className="form-check-input"
                                                        checked={breastData?.symptoms?.nippleErosion === false} disabled
                                                    />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/Remarks.png"} alt="Remarks" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Remarks</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <textarea
                                                    type="text"
                                                    rows="3"
                                                    value={breastData?.symptoms?.remarks} disabled
                                                ></textarea>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="headerTitle ">
                            <div className="titleSection"><h4>Visual Examination</h4></div>
                        </div>
                        <div className="choCancerFeildsSection">
                            <div className="row">
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>&nbsp;</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="checkbox" name="trainedSelfBreastExam" onChange={(e) => setIsScreeningRefused(e.target.checked)} />
                                                    <span>Patient has refused screening</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/trained_individual_for_self_breast_exam.png"} alt="Trained individual" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Trained individual for self breast exam?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="trainedSelfBreastExam" className="form-check-input" checked={breastData?.examination?.trainedSelfBreastExam === true} disabled />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="trainedSelfBreastExam" className="form-check-input" checked={breastData?.examination?.trainedSelfBreastExam === false} disabled />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/Any_breast_related_symptoms.png"} alt="Breast symptoms" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Swelling in armpit?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        name="mat-checkbox-6-input"
                                                        checked={breastData?.examination?.armpitSwellingLeft === 'L'} disabled
                                                    />
                                                    <span>Left</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        name="mat-checkbox-7-input"
                                                        checked={breastData?.examination?.armpitSwellingLeft === 'R'} disabled
                                                    />
                                                    <span>Right</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {breastSymptoms.map((symptom) => (
                                    <div className="col-xs-12 col-md-4" key={symptom.name}>
                                        <div className="SymptomsFeild">
                                            <div className="symptomsImg">
                                                <img src={"/images/Any_breast_related_symptoms.png"} alt={symptom.label} />
                                            </div>
                                            <div className="symptomsDetaisl">
                                                <div className="symbtomsLabel">
                                                    <p>{symptom.label}</p>
                                                </div>
                                                <div className="symtomeFeildsForm">
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name={symptom.name}
                                                            className="form-check-input"
                                                            checked={breastData?.examination?.[symptom.viewname] === true} disabled
                                                        />
                                                        <span>Yes</span>
                                                    </label>
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            name={symptom.name}
                                                            className="form-check-input"
                                                            checked={breastData?.examination?.[symptom.viewname] === false} disabled
                                                        />
                                                        <span>No</span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <div className="col-xs-12 col-md-12">
                                    <div className="SymptomsFeild remarksRow">
                                        <div className="symptomsImg">
                                            <img src={"/images/Remarks.png"} alt="Remarks" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Remarks</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <textarea
                                                    type="text"
                                                    rows="3"
                                                    value={breastData?.examination?.remarks} disabled
                                                ></textarea>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="breastBoxSection">
                            <div className="headerSectionBreastCHO">
                                <div className="feildsArea">
                                    <select disabled>
                                        <option value="">Select Findings</option>
                                    </select>
                                </div>
                                <div className="breastheadings">
                                    <h5>Select findings from dropdown and mark the points below.</h5>
                                </div>
                            </div>
                            <div className="breastCanserCheck">
                                <div className="leftBreast">
                                    <h5>Right</h5>
                                    <div className="wrapper">
                                        <div className="ellipse" />
                                        <div className="line vertical" />
                                        <div className="line horizontal" />
                                        {/* {dotIds.map((dot) => renderDot("right", dot))}
                                        {renderDot("right", "dot13", true)} */}
                                        {dotIds.map((dot, index) => renderDot("right", dot, false, index + 1))}
                                        {renderDot("right", "dot13", true, dotIds.length + 1)}
                                    </div>
                                </div>
                                <div className="rightBreast">
                                    <h5>Left</h5>
                                    <div className="wrapper">
                                        <div className="ellipse" />
                                        <div className="line vertical" />
                                        <div className="line horizontal" />
                                        {/* {dotIds.map((dot) => renderDot("left", dot))}
                                        {renderDot("left", "dot13", true)} */}
                                        {dotIds.map((dot, index) => renderDot("left", dot, false, index + 1))}
                                        {renderDot("left", "dot13", true, dotIds.length + 1)}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="headerTitle ">
                            <div className="titleSection"><h4>Referral</h4></div>
                        </div>
                        <div className="choCancerFeildsSection">
                            <div className="row">
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/Refer.png"} alt="Refer" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Refer</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="breastRefer" className="form-check-input" checked={breastData?.referral?.referred === true} disabled />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="breastRefer" className="form-check-input" checked={breastData?.referral?.referred === false} disabled />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-6">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/Refer.png"} alt="Refer" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Refer to {referBreast === "Yes" && <span className="text-danger">*</span>} </p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <select
                                                    value={breastData?.referral?.referredTo} disabled
                                                >
                                                    <option value="">Select</option>
                                                    <option value="PHC">PHC</option>
                                                    <option value="CHC">CHC</option>
                                                    <option value="District hospital">District Hospital</option>
                                                    <option value="Others">Other</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>If other, specify</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <input
                                                    type="text"
                                                    value={breastData?.referral?.referredToIfOther}
                                                    disabled
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-12">
                                    <div className="SymptomsFeild remarksRow">
                                        <div className="symptomsImg">
                                            <img src={"/images/Remarks.png"} alt="Remarks" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Remarks</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <textarea type="text" rows="3" value={breastData?.referral?.remarks} disabled ></textarea>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
            <div className="separator"></div>
        </>
    );
};
